(function () {
  'use strict';

  var ARC = {
    desktop: { radiusFactor: 0.79, angleStep: 12.2, minWidth: 1200, verticalDrop: 11, minScale: 0.78, scaleReduction: 0.075 },
    tablet: { radiusFactor: 0.725, angleStep: 18.5, minWidth: 768, verticalDrop: 10, minScale: 0.74, scaleReduction: 0.08 },
    mobile: { radiusFactor: 0.65, angleStep: 38, minWidth: 0, verticalDrop: 8, minScale: 0.68, scaleReduction: 0.1 }
  };

  /* Panels stay fully opaque at every depth (opacity is never used to create
     depth — see updateItems). Brightness/saturation stand in for the old
     opacity fade so de-emphasised panels still read as solid objects. */
  var BRIGHTNESS_STOPS = [
    [0, 1],
    [1, 0.91],
    [2, 0.8],
    [3.25, 0.74]
  ];
  var SATURATE_STOPS = [
    [0, 1],
    [1, 0.94],
    [2, 0.89],
    [3.25, 0.86]
  ];
  var SPOTLIGHT_STOPS = [
    [0, 1],
    [1, 0.8],
    [2, 0.61],
    [3, 0.4],
    [4, 0.18]
  ];

  /* Panels beyond this fractional distance are hidden outright (visibility,
     not opacity) rather than left as a faint see-through ghost. Capped so
     that no visible panel is ever rotated close to its own backface. */
  var MAX_VISIBLE_DISTANCE = 3.25;

  function interpolate(distance, stops) {
    for (var i = 0; i < stops.length - 1; i++) {
      var a = stops[i];
      var b = stops[i + 1];
      if (distance <= b[0]) {
        var t = (distance - a[0]) / (b[0] - a[0]);
        return a[1] + (b[1] - a[1]) * t;
      }
    }
    return stops[stops.length - 1][1];
  }

  function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
  }

  function arcForWidth(width) {
    if (width >= ARC.desktop.minWidth) return ARC.desktop;
    if (width >= ARC.tablet.minWidth) return ARC.tablet;
    return ARC.mobile;
  }

  function pad2(n) {
    return String(n).padStart(2, '0');
  }

  function shortestOffset(index, effectiveIndex, count) {
    var offset = index - effectiveIndex;
    var half = count / 2;
    while (offset > half) offset -= count;
    while (offset < -half) offset += count;
    return offset;
  }

  function DBRCurvedGallery(section) {
    this.section = section;
    this.destroyed = false;
  }

  DBRCurvedGallery.prototype.init = function () {
    this.cacheElements();
    if (!this.stage || !this.track || !this.items.length) return;

    this.itemCount = this.items.length;
    this.activeIndex = 0;
    this.dragVisualIndex = 0;
    this.enableDrag = this.section.dataset.enableDrag !== 'false';

    /* Single pointer-drag controller -- dragVisualIndex is the sole source
       of visual truth while dragging (never mixed with a second fractional
       variable). See onPointerDown/onPointerMove/onPointerUp. */
    this.pointerId = null;
    this.pointerStartX = 0;
    this.pointerStartY = 0;
    this.pointerCurrentX = 0;
    this.pointerCurrentY = 0;
    this.dragStartIndex = 0;
    this.dragDeltaX = 0;
    this.isPointerDown = false;
    this.isDragging = false;
    this.suppressClick = false;
    this.dragFrame = null;
    this.dragThreshold = 7;
    this.horizontalIntent = null;

    /* Gesture-based wheel/trackpad controller (see onWheel) — one physical
       gesture, however many individual wheel events it fires, must commit
       exactly one navigation step. */
    this.wheelAccumulator = 0;
    this.wheelGestureActive = false;
    this.wheelGestureCommitted = false;
    this.wheelGestureTimer = null;
    this.wheelGestureEndDelay = 180;
    this.wheelThreshold = 42;
    this.wheelLockedUntil = 0;
    this.wheelPostCommitLock = 360;
    this.lastWheelDirection = 0;

    this.resizeRafId = null;

    this.pointerOverGallery = false;
    this.isSufficientlyVisible = false;
    this.intersectionObserver = null;
    this.hoveredArtworkCount = 0;

    this.applyRatioModifiers();
    this.bindEvents();
    this.calculateLayout();
    this.updateItems();

    this.section.classList.add('js-enabled');
  };

  DBRCurvedGallery.prototype.cacheElements = function () {
    this.stage = this.section.querySelector('[data-gallery-stage]');
    this.track = this.section.querySelector('[data-gallery-track]');
    this.items = Array.prototype.slice.call(this.section.querySelectorAll('[data-gallery-item]'));
    this.links = this.items.map(function (item) {
      return item.querySelector('[data-artwork-link]');
    });
    this.previousButton = this.section.querySelector('[data-gallery-prev]');
    this.nextButton = this.section.querySelector('[data-gallery-next]');
    this.counter = this.section.querySelector('[data-gallery-counter]');
    this.instruction = this.section.querySelector('[data-gallery-hint]');
  };

  /* Wide landscape works (ratio > 1.25) get a slightly reduced panel height
     so they can't cover neighbouring panels along the arc. Computed once —
     never during drag. */
  DBRCurvedGallery.prototype.applyRatioModifiers = function () {
    this.items.forEach(function (item) {
      var ratio = parseFloat(item.style.getPropertyValue('--artwork-ratio')) || 0.8;
      if (ratio > 1.25) {
        /* Cap rendered WIDTH (not just height) so very wide landscape works
           can't grow wide enough to reach into a neighbouring panel's
           position along the arc, no matter how extreme the ratio gets. */
        var mod = Math.max(0.42, Math.min(1, 0.82 / ratio));
        item.style.setProperty('--panel-height-mod', String(mod));
      }
    });
  };

  DBRCurvedGallery.prototype.calculateLayout = function () {
    var rect = this.stage.getBoundingClientRect();
    this.stageWidth = rect.width || this.stage.offsetWidth || 1;
    this.arcConfig = arcForWidth(window.innerWidth);
    this.radius = this.stageWidth * this.arcConfig.radiusFactor;
    this.dragStepDistance = Math.max(this.stageWidth * 0.22, 160);
    /* A panel must never be allowed to rotate anywhere near its own
       backface. 88° (not 90°) leaves a small safety margin. */
    this.visibleDistance = Math.min(MAX_VISIBLE_DISTANCE, 88 / this.arcConfig.angleStep);
  };

  DBRCurvedGallery.prototype.updateItems = function () {
    var count = this.itemCount;
    var effectiveIndex = this.dragVisualIndex;
    var cfg = this.arcConfig;
    var radius = this.radius;
    var visibleDistance = this.visibleDistance;

    this.items.forEach(function (item, i) {
      var offset = shortestOffset(i, effectiveIndex, count);
      var distance = Math.abs(offset);
      var isVisible = distance <= visibleDistance;
      var theta = offset * cfg.angleStep;
      var thetaRad = (theta * Math.PI) / 180;
      var x = Math.sin(thetaRad) * radius;
      var z = Math.cos(thetaRad) * radius - radius;
      var rotationY = -theta;
      var y = Math.pow(distance, 1.7) * cfg.verticalDrop;
      var scale = Math.max(cfg.minScale, 1 - distance * cfg.scaleReduction);

      var brightness = interpolate(Math.min(distance, MAX_VISIBLE_DISTANCE), BRIGHTNESS_STOPS);
      var saturate = interpolate(Math.min(distance, MAX_VISIBLE_DISTANCE), SATURATE_STOPS);
      var spotlight = interpolate(Math.min(distance, 4), SPOTLIGHT_STOPS);

      item.style.transform =
        'translate3d(calc(-50% + ' + x + 'px), calc(-50% + ' + y + 'px), ' + z + 'px) rotateY(' + rotationY + 'deg) scale(' + scale + ')';

      /* Panels are always fully opaque — de-emphasis comes from brightness,
         saturation, scale and depth only, never from transparency, so a
         de-emphasised panel can never let a neighbour show through it. */
      item.style.opacity = isVisible ? '1' : '0';
      item.style.visibility = isVisible ? 'visible' : 'hidden';
      item.style.pointerEvents = isVisible ? '' : 'none';
      item.style.filter = 'brightness(' + brightness + ') saturate(' + saturate + ')';
      item.style.setProperty('--spotlight-opacity', String(spotlight));

      /* Depth-ranked stacking: the active panel is always highest, each
         ring of neighbours sits strictly below the ring nearer the centre,
         so a farther panel can never render over a nearer one. */
      var depthRank = Math.max(0, 100 - Math.round(distance * 10));
      item.style.zIndex = String(depthRank);
      item.classList.toggle('is-active', i === this.activeIndex && !this.isDragging);

      var link = this.links[i];
      if (link) link.setAttribute('aria-hidden', isVisible ? 'false' : 'true');
    }, this);

    if (this.counter) {
      this.counter.textContent = pad2(this.activeIndex + 1) + ' / ' + pad2(count);
    }
  };

  DBRCurvedGallery.prototype.setActive = function (index, options) {
    options = options || {};
    this.activeIndex = ((Math.round(index) % this.itemCount) + this.itemCount) % this.itemCount;
    this.dragVisualIndex = this.activeIndex;
    this.updateItems();
    if (!options.silent) this.dismissHint();
  };

  DBRCurvedGallery.prototype.next = function () {
    this.setActive(this.activeIndex + 1);
  };

  DBRCurvedGallery.prototype.previous = function () {
    this.setActive(this.activeIndex - 1);
  };

  DBRCurvedGallery.prototype.dismissHint = function () {
    if (this.instruction) this.instruction.classList.add('is-hidden');
  };

  DBRCurvedGallery.prototype.bindEvents = function () {
    var self = this;

    this._onPrevClick = function () {
      self.previous();
    };
    this._onNextClick = function () {
      self.next();
    };
    if (this.previousButton) this.previousButton.addEventListener('click', this._onPrevClick);
    if (this.nextButton) this.nextButton.addEventListener('click', this._onNextClick);

    this._onKeyDown = this.onKeyDown.bind(this);
    this.stage.addEventListener('keydown', this._onKeyDown);

    /* Keyboard control also works when the pointer is simply hovering the
       gallery (not just when the stage has DOM focus) — see onDocumentKeyDown. */
    this._onPointerEnter = function () {
      self.pointerOverGallery = true;
    };
    this._onPointerLeave = function () {
      self.pointerOverGallery = false;
    };
    this.stage.addEventListener('pointerenter', this._onPointerEnter);
    this.stage.addEventListener('pointerleave', this._onPointerLeave);

    this.isSufficientlyVisible = false;
    if ('IntersectionObserver' in window) {
      this._onIntersect = function (entries) {
        var entry = entries[entries.length - 1];
        self.isSufficientlyVisible = !!entry && entry.intersectionRatio >= 0.4;
      };
      this.intersectionObserver = new IntersectionObserver(this._onIntersect, { threshold: [0, 0.4, 1] });
      this.intersectionObserver.observe(this.section);
    } else {
      /* No IntersectionObserver support: fall back to always-eligible so
         hover-based keyboard control still works. */
      this.isSufficientlyVisible = true;
    }

    this._onDocumentKeyDown = this.onDocumentKeyDown.bind(this);
    document.addEventListener('keydown', this._onDocumentKeyDown);

    this._onLinkClickHandlers = this.items.map(function (item, index) {
      var link = self.links[index];
      var handler = function (event) {
        self.onLinkClick(event, index);
      };
      if (link) link.addEventListener('click', handler);
      return handler;
    });

    /* Capture-phase guard: fires before any per-link click handler further
       down the tree, so a drag-release click can never open or centre an
       artwork no matter which element it lands on. */
    this._onCaptureClick = function (event) {
      if (self.suppressClick) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    };
    this.stage.addEventListener('click', this._onCaptureClick, true);

    /* Delegated hover detection for the hover-only arrow controls: uses
       pointerover/pointerout (which bubble) with closest() matching rather
       than attaching a listener to every artwork link individually. */
    this.hoveredArtworkCount = 0;
    this._onArtworkPointerOver = function (event) {
      var link = event.target.closest ? event.target.closest('[data-artwork-link]') : null;
      if (!link) return;
      var fromLink = event.relatedTarget && event.relatedTarget.closest ? event.relatedTarget.closest('[data-artwork-link]') : null;
      if (fromLink === link) return;
      self.hoveredArtworkCount++;
      self.section.classList.add('has-artwork-hover');
    };
    this._onArtworkPointerOut = function (event) {
      var link = event.target.closest ? event.target.closest('[data-artwork-link]') : null;
      if (!link) return;
      var toLink = event.relatedTarget && event.relatedTarget.closest ? event.relatedTarget.closest('[data-artwork-link]') : null;
      if (toLink === link) return;
      self.hoveredArtworkCount = Math.max(0, self.hoveredArtworkCount - 1);
      if (self.hoveredArtworkCount === 0) self.section.classList.remove('has-artwork-hover');
    };
    this.stage.addEventListener('pointerover', this._onArtworkPointerOver);
    this.stage.addEventListener('pointerout', this._onArtworkPointerOut);

    if (this.enableDrag) {
      this._onPointerDown = this.onPointerDown.bind(this);
      this._onPointerMove = this.onPointerMove.bind(this);
      this._onPointerUp = this.onPointerUp.bind(this);
      this._onLostPointerCapture = this.onPointerUp.bind(this);
      this._onDragStart = function (event) {
        event.preventDefault();
      };
      this.stage.addEventListener('pointerdown', this._onPointerDown);
      this.stage.addEventListener('pointermove', this._onPointerMove, { passive: false });
      this.stage.addEventListener('pointerup', this._onPointerUp);
      this.stage.addEventListener('pointercancel', this._onPointerUp);
      this.stage.addEventListener('lostpointercapture', this._onLostPointerCapture);
      this.track.addEventListener('dragstart', this._onDragStart);
    }

    /* Exactly one wheel listener, on the stage only — never on the section,
       track, window or document — so there is never more than one gallery
       wheel handler firing per physical gesture. */
    this._onWheel = this.onWheel.bind(this);
    this.stage.addEventListener('wheel', this._onWheel, { passive: false });

    this._onResize = this.onResize.bind(this);
    window.addEventListener('resize', this._onResize);

    this._onBlockSelect = function (evt) {
      var item = evt.target.closest ? evt.target.closest('[data-gallery-item]') : null;
      if (!item) return;
      var index = parseInt(item.dataset.index, 10);
      if (!isNaN(index)) self.setActive(index, { silent: true });
    };
    this.section.addEventListener('shopify:block:select', this._onBlockSelect);
  };

  function isEditableTarget(target) {
    if (!target) return false;
    var tag = target.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON') return true;
    if (target.isContentEditable) return true;
    return false;
  }

  /* Shared by the stage's own keydown (when the gallery has DOM focus) and
     the document-level listener (when the pointer is merely hovering it) —
     both must apply the exact same guards so keyboard nav behaves
     identically either way. event.repeat is ignored so holding a key down
     never fires more than the browser's own single logical press per
     dispatch cycle would otherwise imply — one physical tap, one step. */
  DBRCurvedGallery.prototype.handleNavigationKey = function (event) {
    if (event.repeat) return false;
    if (isEditableTarget(event.target)) return false;

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.next();
      return true;
    }
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.previous();
      return true;
    }
    if (event.key === 'Home') {
      event.preventDefault();
      this.setActive(0);
      return true;
    }
    if (event.key === 'End') {
      event.preventDefault();
      this.setActive(this.itemCount - 1);
      return true;
    }
    return false;
  };

  DBRCurvedGallery.prototype.onKeyDown = function (event) {
    this.handleNavigationKey(event);
  };

  /* Lets a visitor use the arrow keys the moment their pointer is over the
     exhibition, without first needing to understand that they must click
     the stage to focus it. Only engages when this gallery is both
     reasonably on-screen and either hovered or already focused, so it never
     hijacks arrow keys used for typing or scrolling elsewhere on the page,
     and never competes with another gallery instance. */
  DBRCurvedGallery.prototype.onDocumentKeyDown = function (event) {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    /* Covers the stage itself AND any focusable descendant (an artwork
       link, for instance) -- the stage's own keydown listener already
       receives the bubbled event in both cases, so this guard must exclude
       the whole subtree, not just the stage element, or a focused artwork
       link would cause both listeners to fire and double-advance. */
    if (this.stage.contains(document.activeElement)) return;
    if (!this.isSufficientlyVisible) return;
    if (!this.pointerOverGallery) return;
    if (document.querySelector('[aria-modal="true"]')) return;

    this.handleNavigationKey(event);
  };

  DBRCurvedGallery.prototype.onLinkClick = function (event, index) {
    if (this.suppressClick) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    /* Keyboard-activated clicks (Enter/Space on a focused link) report
       detail === 0 in every major browser — let those navigate directly
       without requiring the artwork to be centred first. */
    if (event.detail === 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (typeof event.button === 'number' && event.button > 0) return;

    if (index !== this.activeIndex) {
      event.preventDefault();
      this.setActive(index);
    }
  };

  /* ---------- Mouse/pointer drag: one controller, one visual-position
     source of truth ----------
     dragVisualIndex is updated only inside renderDragPosition (itself only
     ever scheduled from onPointerMove, at most once per animation frame).
     activeIndex is never touched until the drag actually commits in
     onPointerUp, so there is exactly one place that decides the final
     index and exactly one place that renders the in-between arc position —
     no competing timers, no second fractional variable. */

  DBRCurvedGallery.prototype.onPointerDown = function (event) {
    if (typeof event.button === 'number' && event.button !== 0) return;
    if (event.target.closest && event.target.closest('.dbr-curved-gallery__controls, .dbr-curved-gallery__footer-link, .dbr-curved-gallery__hover-navigation')) return;

    if (this.dragFrame) {
      cancelAnimationFrame(this.dragFrame);
      this.dragFrame = null;
    }

    this.isPointerDown = true;
    this.isDragging = false;
    this.suppressClick = false;
    this.horizontalIntent = null;
    this.pointerId = event.pointerId;
    this.pointerStartX = event.clientX;
    this.pointerStartY = event.clientY;
    this.pointerCurrentX = event.clientX;
    this.pointerCurrentY = event.clientY;
    this.dragStartIndex = this.activeIndex;
    this.dragVisualIndex = this.activeIndex;
    this.dragDeltaX = 0;

    this.section.classList.add('dbr-curved-gallery--pointer-down');
  };

  DBRCurvedGallery.prototype.onPointerMove = function (event) {
    if (!this.isPointerDown) return;
    this.pointerCurrentX = event.clientX;
    this.pointerCurrentY = event.clientY;
    var deltaX = this.pointerCurrentX - this.pointerStartX;
    var deltaY = this.pointerCurrentY - this.pointerStartY;

    if (this.horizontalIntent === null) {
      if (Math.abs(deltaX) >= this.dragThreshold && Math.abs(deltaX) > Math.abs(deltaY) * 1.15) {
        this.horizontalIntent = true;
      } else if (Math.abs(deltaY) > this.dragThreshold) {
        this.horizontalIntent = false;
      }
    }

    if (this.horizontalIntent !== true) return;

    if (!this.isDragging) {
      this.isDragging = true;
      this.suppressClick = true;
      if (this.stage.setPointerCapture) {
        try {
          this.stage.setPointerCapture(this.pointerId);
        } catch (e) {
          /* capture is an enhancement only */
        }
      }
      this.stage.classList.add('is-dragging');
      this.section.classList.add('is-dragging');
    }

    event.preventDefault();
    this.dragDeltaX = deltaX;
    this.scheduleDragRender();
  };

  /* Exactly one animation frame is ever pending at a time -- a second
     pointermove arriving before the scheduled frame runs just updates
     dragDeltaX and returns, it never queues a second frame. */
  DBRCurvedGallery.prototype.scheduleDragRender = function () {
    if (this.dragFrame) return;
    var self = this;
    this.dragFrame = requestAnimationFrame(function () {
      self.dragFrame = null;
      self.renderDragPosition();
    });
  };

  DBRCurvedGallery.prototype.renderDragPosition = function () {
    if (!this.isDragging) return;
    this.dragVisualIndex = this.dragStartIndex - this.dragDeltaX / this.dragStepDistance;
    this.updateItems();
  };

  DBRCurvedGallery.prototype.onPointerUp = function (event) {
    if (!this.isPointerDown) return;
    this.isPointerDown = false;
    this.section.classList.remove('dbr-curved-gallery--pointer-down');

    if (this.stage.releasePointerCapture && this.pointerId !== null) {
      try {
        this.stage.releasePointerCapture(this.pointerId);
      } catch (e) {
        /* no-op */
      }
    }

    if (this.dragFrame) {
      cancelAnimationFrame(this.dragFrame);
      this.dragFrame = null;
    }

    if (this.isDragging) {
      var endX = event && event.clientX !== undefined ? event.clientX : this.pointerCurrentX;
      var travelledPx = this.pointerStartX - endX;
      var travelledSteps = travelledPx / this.dragStepDistance;
      /* Round to the nearest artwork from the continuous drag position --
         a movement under half a step naturally rounds back to where the
         drag started, so no separate "too small to count" branch is
         needed. Committed exactly once, here, and nowhere else. */
      var targetIndex = Math.round(this.dragStartIndex + travelledSteps);
      this.setActive(targetIndex);
    } else {
      this.dragVisualIndex = this.activeIndex;
      this.updateItems();
    }

    var self = this;
    requestAnimationFrame(function () {
      self.stage.classList.remove('is-dragging');
      self.section.classList.remove('is-dragging');
    });

    this.isDragging = false;
    this.horizontalIntent = null;

    setTimeout(function () {
      self.suppressClick = false;
    }, 0);
  };

  /* ---------- Wheel / trackpad: one physical gesture = one artwork ----------
     A single trackpad swipe or Shift+wheel gesture fires many individual
     'wheel' events (and, on release, an inertial tail of more events at
     shrinking magnitude). Treating each event as its own navigation input
     causes exactly the reported bug: one swipe skips two artworks. Instead
     every event belonging to the same physical gesture accumulates into
     wheelAccumulator; the gesture commits at most one setActive() call, then
     locks itself out — including through its own inertial tail — until a
     new, separate physical gesture begins. */
  DBRCurvedGallery.prototype.onWheel = function (event) {
    var horizontalDelta = Math.abs(event.deltaX) > 0 ? event.deltaX : event.shiftKey && Math.abs(event.deltaY) > 0 ? event.deltaY : 0;

    if (horizontalDelta === 0) return;
    if (Math.abs(event.deltaY) > Math.abs(horizontalDelta) * 1.15) return;

    /* Only clearly horizontal intent reaches this point — ordinary vertical
       page scrolling over the gallery is left completely alone. */
    event.preventDefault();

    var normalisedDelta = horizontalDelta;
    if (event.deltaMode === 1) {
      normalisedDelta *= 16; // DOM_DELTA_LINE
    } else if (event.deltaMode === 2) {
      normalisedDelta *= this.stageWidth || 800; // DOM_DELTA_PAGE
    }
    normalisedDelta = clamp(normalisedDelta, -80, 80);

    var direction = normalisedDelta > 0 ? 1 : -1;

    if (!this.wheelGestureActive) {
      this.wheelGestureActive = true;
      this.wheelGestureCommitted = false;
      this.wheelAccumulator = 0;
      this.lastWheelDirection = direction;
    }

    this.wheelAccumulator += normalisedDelta;

    var self = this;
    if (this.wheelGestureTimer) clearTimeout(this.wheelGestureTimer);
    this.wheelGestureTimer = setTimeout(function () {
      self.wheelGestureActive = false;
      self.wheelGestureCommitted = false;
      self.wheelAccumulator = 0;
      self.lastWheelDirection = 0;
      self.wheelGestureTimer = null;
    }, this.wheelGestureEndDelay);

    if (!this.wheelGestureCommitted && Math.abs(this.wheelAccumulator) >= this.wheelThreshold && performance.now() >= this.wheelLockedUntil) {
      if (this.wheelAccumulator > 0) this.next();
      else this.previous();

      this.wheelGestureCommitted = true;
      this.wheelLockedUntil = performance.now() + this.wheelPostCommitLock;
    }
    /* Once committed, further horizontal events belonging to this same
       gesture (including its inertial tail) are still consumed above via
       preventDefault/accumulation, but wheelGestureCommitted blocks any
       further setActive() call until a new gesture begins. */
  };

  DBRCurvedGallery.prototype.onResize = function () {
    if (this.resizeRafId) return;
    var self = this;
    this.resizeRafId = requestAnimationFrame(function () {
      self.resizeRafId = null;
      self.calculateLayout();
      self.updateItems();
    });
  };

  DBRCurvedGallery.prototype.destroy = function () {
    if (this.destroyed) return;
    this.destroyed = true;

    if (this.dragFrame) cancelAnimationFrame(this.dragFrame);
    if (this.resizeRafId) cancelAnimationFrame(this.resizeRafId);
    if (this.wheelGestureTimer) clearTimeout(this.wheelGestureTimer);
    this.wheelGestureActive = false;
    this.wheelGestureCommitted = false;
    this.wheelAccumulator = 0;
    if (this.isPointerDown && this.stage && this.stage.releasePointerCapture && this.pointerId !== null) {
      try {
        this.stage.releasePointerCapture(this.pointerId);
      } catch (e) {
        /* no-op */
      }
    }

    if (this.previousButton) this.previousButton.removeEventListener('click', this._onPrevClick);
    if (this.nextButton) this.nextButton.removeEventListener('click', this._onNextClick);
    if (this.stage) {
      this.stage.removeEventListener('keydown', this._onKeyDown);
      this.stage.removeEventListener('wheel', this._onWheel);
      this.stage.removeEventListener('pointerenter', this._onPointerEnter);
      this.stage.removeEventListener('pointerleave', this._onPointerLeave);
      this.stage.removeEventListener('click', this._onCaptureClick, true);
      this.stage.removeEventListener('pointerover', this._onArtworkPointerOver);
      this.stage.removeEventListener('pointerout', this._onArtworkPointerOut);
      if (this.enableDrag) {
        this.stage.removeEventListener('pointerdown', this._onPointerDown);
        this.stage.removeEventListener('pointermove', this._onPointerMove);
        this.stage.removeEventListener('pointerup', this._onPointerUp);
        this.stage.removeEventListener('pointercancel', this._onPointerUp);
        this.stage.removeEventListener('lostpointercapture', this._onLostPointerCapture);
      }
    }
    if (this.track && this.enableDrag) this.track.removeEventListener('dragstart', this._onDragStart);
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    document.removeEventListener('keydown', this._onDocumentKeyDown);

    var self = this;
    this.links.forEach(function (link, index) {
      if (link && self._onLinkClickHandlers && self._onLinkClickHandlers[index]) {
        link.removeEventListener('click', self._onLinkClickHandlers[index]);
      }
    });

    window.removeEventListener('resize', this._onResize);
    if (this.section) {
      this.section.removeEventListener('shopify:block:select', this._onBlockSelect);
      this.section.classList.remove('js-enabled', 'is-dragging', 'dbr-curved-gallery--pointer-down');
      delete this.section.dataset.dbrGalleryInit;
    }
  };

  var instances = {};

  function initSection(section) {
    if (!section) return;
    /* Destroy any previous instance first (rather than guarding with an
       init flag) so a Shopify Theme Editor reload always leaves exactly one
       live listener set, never a stacked duplicate. */
    if (section._dbrCurvedGallery) {
      section._dbrCurvedGallery.destroy();
      section._dbrCurvedGallery = null;
    }
    var gallery = new DBRCurvedGallery(section);
    gallery.init();
    section._dbrCurvedGallery = gallery;
    instances[section.dataset.sectionId] = gallery;
  }

  document.querySelectorAll('[data-dbr-curved-gallery][data-section-id]').forEach(initSection);

  document.addEventListener('shopify:section:load', function (evt) {
    var section = evt.target.querySelector ? evt.target.querySelector('[data-dbr-curved-gallery][data-section-id]') : null;
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (evt) {
    var sectionId = evt.detail && evt.detail.sectionId;
    if (!sectionId || !instances[sectionId]) return;
    instances[sectionId].destroy();
    delete instances[sectionId];
  });

  document.addEventListener('shopify:section:select', function (evt) {
    var sectionId = evt.detail && evt.detail.sectionId;
    var gallery = sectionId && instances[sectionId];
    if (gallery) {
      gallery.calculateLayout();
      gallery.updateItems();
    }
  });
})();
